import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  TextFieldProps,
  Box,
} from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { useEffect, useMemo, useState } from 'react';
import useUserData, { UserType } from 'hooks/useUserData';
import { useSuggestionContactsQuery } from '@buildwise/libs/company-admin';
import { uniqBy, sortBy } from 'lodash';
import { ArticleSourceType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import { useConfigEntryOptionsQuery } from '@buildwise/libs/configurations';
import GroupLineIcon from 'remixicon-react/GroupLineIcon';
import Dot from 'components/common/dot/Dot';

type PostMultiSelectCustomFieldProps = {
  autoFocus?: boolean;
  field: ConfigEntryType;
  TextFieldProps?: TextFieldProps;
  articleType: ArticleSourceType;
};

type OptionType = { label: string; value: string; usersCount?: number };

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

function TargetGroupsField({
  autoFocus,
  field,
  TextFieldProps,
  articleType,
}: PostMultiSelectCustomFieldProps) {
  const { companyId } = useUserData();
  const [inputValue, setInputValue] = useState('');
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext();
  const selectedOptions: string[] = useWatch({
    control,
    name: field?.entryCode,
  });
  const defaultSelectedOptions: OptionType[] = useMemo(() => {
    return selectedOptions?.map((item) => ({ label: item, value: item })) || [];
  }, [selectedOptions]);
  const [fieldValue, setFieldValue] = useState<OptionType[]>(defaultSelectedOptions);
  // const { suggestionContacts, isLoading } = useSuggestionContactsQuery({
  //   companyId,
  //   shouldFetch: articleType === ArticleSourceType.SUGGESTION,
  // });
  const { activeUniqueOrderedOptions: departments, isLoading: isLoadingDepartments } =
    useConfigEntryOptionsQuery({
      companyId,
      configEntry: field,
      shouldFetch: true,
      defaultOptions: defaultSelectedOptions,
    });
  const { data: peopleData, isLoading: peopleLoading } =
    useSearchItemQuery<UsersSearchResponseType>(
      {
        searchType: 'users',
        operator: 'or',
        filterBy: {
          filters: [
            {
              filterType: 'name',
              filterValue: 'ALL',
            },
          ],
        },
      },
      articleType === ArticleSourceType.QUESTION,
    );
  const targetGroupsOptions: OptionType[] = useMemo(() => {
    if (articleType === ArticleSourceType.SUGGESTION) {
      return departments;
    } else {
      const departmentCounts = (peopleData?.userResponse || [])
        .flatMap((user) => user?.department || [])
        .reduce((acc, dept) => {
          acc[dept] = (acc[dept] || 0) + 1;
          return acc;
        }, {});

      const departmentOptions = departments.map((dept) => ({
        ...dept,
        usersCount: departmentCounts[dept.value] || 0,
      }));

      return sortBy(uniqBy(departmentOptions, 'value'), 'label');
    }
  }, [departments, peopleData]);

  useEffect(() => {
    if (targetGroupsOptions?.length && selectedOptions?.length) {
      setFieldValue(targetGroupsOptions.filter((item) => selectedOptions?.includes(item.value)));
    }
  }, [targetGroupsOptions]);

  const handleSelect = (_: unknown, values: OptionType[]) => {
    let selectedValues: string[] = values.map((option) => option?.value);
    setValue(field.entryCode, selectedValues, { shouldDirty: true, shouldValidate: true });
    setFieldValue(values);
  };

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState, field: { ref } }) => (
          <FormControl fullWidth>
            <Autocomplete
              disablePortal
              id={field.entryCode}
              multiple
              limitTags={3}
              disableCloseOnSelect
              inputValue={inputValue}
              onInputChange={(_, value) => {
                setInputValue(value);
              }}
              onChange={handleSelect}
              options={targetGroupsOptions}
              autoHighlight
              value={fieldValue.length ? fieldValue : defaultSelectedOptions}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText={isLoadingDepartments || peopleLoading ? 'Loading...' : 'No Data Found'}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  {option.usersCount ? (
                    <>
                      {option.label}
                      <Dot />
                      <Box display="flex" alignItems="center" title="Department Users">
                        <GroupLineIcon size={16} />
                        &nbsp;
                        {option.usersCount}
                      </Box>
                    </>
                  ) : (
                    option.label
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus={autoFocus}
                  inputRef={ref}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingDepartments || peopleLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  {...TextFieldProps}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={
                      option.usersCount ? (
                        <Box display="flex" alignItems="center">
                          {option.label}
                          <Dot />
                          <Box display="flex" alignItems="center" title="Department Users">
                            <GroupLineIcon size={16} />
                            &nbsp;
                            {option.usersCount}
                          </Box>
                        </Box>
                      ) : (
                        option.label
                      )
                    }
                    deleteIcon={<CloseLineIcon size={20} />}
                  />
                ))
              }
              disabled={isSubmitting}
            />
          </FormControl>
        )}
        name={`${field.entryCode}`}
      />
    </>
  );
}

export default TargetGroupsField;
